import i18next from "i18next";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import i18n from "../../i18n";

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  children?: React.ReactNode;
  width?: string;
  header?: string | React.ReactNode;
  customStyle?: string;
  headerCustomStyle?: string;
  ariaLabel?: string;
  showClose?: boolean;
  isPortal?: boolean;
  modalDescriptionClasses?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  width = "max-w-lg",
  header,
  customStyle,
  headerCustomStyle,
  ariaLabel,
  showClose = true,
  isPortal = true,
  modalDescriptionClasses = "",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only close if onClose exists:
      if (e.key === "Escape" && onClose) {
        handleClose();
      }
    };

    if (isOpen) {
      setShowModal(true);
      setAnimateOut(false);
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else if (!isOpen && showModal) {
      setAnimateOut(true);
      setTimeout(() => setShowModal(false), 200);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleBackdropClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (e.target === e.currentTarget && onClose) {
      handleClose();
    }
  };

  const handleClose = () => {
    setAnimateOut(true);
    setTimeout(() => {
      setShowModal(false);
      onClose?.();
    }, 200);
  };

  const ariaAttributes = ariaLabel
    ? { "aria-label": ariaLabel }
    : header
      ? { "aria-labelledby": "modal-title" }
      : {};

  const lang = i18n?.language?.slice(0, 2);

  if (!isOpen && !showModal) return null;

  const modalContent = (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
      onClick={handleBackdropClick}
      role="dialog"
      dir={i18next.language?.slice(0, 2) == "en" ? "ltr" : "rtl"}
      aria-modal="true"
      {...ariaAttributes}
    >
      <div
        className={`lang-${lang} hideScroll relative z-40 flex max-h-[95dvh] min-w-80 grow flex-col overflow-auto ${header ? "gap-6" : ""} rounded-2xl bg-white px-3 py-4 shadow-toast sm:p-4 max-w-[85vw] lg:${width} ${customStyle} transform transition-all duration-500 ${
          animateOut ? "zoom-out" : "zoom-in"
        }`}
        ref={modalRef}
      >
        {showClose && onClose && (
          <button
            className="text-primary-textFont absolute end-6 top-2 rounded text-4xl focus:outline-none focus:ring-2 focus:ring-primary"
            onClick={handleClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        )}
        <div className={`flex justify-between ${headerCustomStyle} w-[95%]`}>
          {header && (
            <h4
              id="modal-title"
              className="text-primary-textFont max-w-[95%] text-xl font-bold"
            >
              {header}
            </h4>
          )}
        </div>
        <div
          className={`flex grow flex-col gap-4 ${modalDescriptionClasses}`}
          id="modal-description"
        >
          {children}
        </div>
      </div>
    </div>
  );

  return isOpen
    ? isPortal
      ? ReactDOM.createPortal(modalContent, document.body)
      : modalContent
    : null;
};

export default Modal;
